import apiService from './api';
import type { ApiResponse } from '../types';
import type { Numeric } from '@/utils/numeric';

export interface MyPeriodEntry {
  id: number;
  clock_in_time: string;
  clock_out_time: string | null;
  total_hours: Numeric;
  payroll_category: string;
  status: string;
  notes: string | null;
  /** Hours the employee declared from their work schedule — no clock record behind them. */
  missed_clock: boolean;
  missed_clock_reason: string | null;
  approval_status: 'approved' | 'pending' | 'rejected' | 'unsubmitted';
}

export interface MyPeriodDay {
  date: string;
  weekday: number; // 1=Mon…7=Sun
  is_working_day: boolean;
  scheduled_hours: number;
  scheduled_start_time: string | null;
  scheduled_end_time: string | null;
  worked_hours: number;
  shortfall_hours: number;
  /**
   * How much of `shortfall_hours` the employee can declare as worked hours
   * themselves. NOT the same number: a declaration is written into the
   * untouched stretches of the rostered day, so entries that already span the
   * rostered start and finish leave nothing to write even while the day is
   * short. Driving the UI off `shortfall_hours` alone offered claims the
   * server was always going to refuse.
   */
  claimable_hours: number;
  is_public_holiday: boolean;
  holiday_name: string | null;
  is_leave: boolean;
  locked: boolean;
  approval_status: 'approved' | 'pending' | 'rejected' | 'unsubmitted';
  entries: MyPeriodEntry[];
}

export interface MyPeriodData {
  start_date: string;
  end_date: string;
  state: string | null;
  locked: boolean;
  totals: {
    scheduled_hours: number;
    worked_hours: number;
    shortfall_hours: number;
  };
  days: MyPeriodDay[];
}

export interface MissedClockResult {
  /** The first of `entries`; kept for bundles shipped before a day could write several. */
  entry: { id: number; date: string; total_hours: Numeric };
  /** One row per untouched stretch of the rostered day. */
  entries: { id: number; date: string; total_hours: Numeric }[];
  claimed_hours: number;
  scheduled_hours: number;
}

class MyTimesheetService {
  /** The caller's own day-by-day timesheet for a pay period. */
  async getMyPeriod(start: string, end: string): Promise<ApiResponse<MyPeriodData>> {
    const qs = new URLSearchParams({ start, end }).toString();
    return apiService.get(`/timesheet/my-period?${qs}`);
  }

  /**
   * Declare a day's scheduled hours as normal worked time when no clock record
   * exists — "I worked, I just forgot to clock in". Creates an ordinary Base
   * Hourly entry flagged `missed_clock`, still subject to supervisor approval.
   */
  async declareMissedClock(date: string, reason?: string): Promise<ApiResponse<MissedClockResult>> {
    return apiService.post('/timesheet/missed-clock', { date, reason: reason || undefined });
  }

  /** Withdraw a declaration made in error, while the period is still unsubmitted. */
  async withdrawMissedClock(entryId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/timesheet/missed-clock/${entryId}`);
  }
}

export default new MyTimesheetService();
